export const rnaModule = {
  id: "rna",
  title: "RNA Analysis",
  sections: [
    {
      type: "text",
      content: `<h3>RT-PCR</h3>
        <p>Real-time PCR (RT-PCR) or quantitative PCR (qPCR) is a method used to analyse RNA expression, to measure quantitative changes in gene expression in response to changes in the cells environment. </p>
        <p>After RNA has been isolated, reverse transcriptase is used to translate the RNA into complementary DNA (cDNA) copies for further PCR amplification and quantification. </p>
        <p>The cDNA is then mixed with either SYBR green fluorescent dye or a TaqMan Probe. SYBR green emits a strong fluorescent light when it connects to double stranded DNA (dsDNA). This means that the more dsDNA is present in a sample, the stronger the SYBR green signal will be.</p> 
        <p>By amplifying the cDNA via PCR amplification, and quantifying the strength of the fluorescent signal after every amplification cycle, it can be plotted on a graph, which compares signal strength against number of amplification cycles (Ct, the number of cycles needed for a fluorescent signal to be detected). </p>
        <p>If the polymerase and primers are effective, this produces a exponential curve, from the time in which the machine used starts to detect the sample until the signal saturates the detector (meaning that the detector can no longer distinguish the gains in fluorescent light). By comparing these results to another sample result one can calculate the relative expression.</p>
        <p>TaqMan probes can be used instead of SYBR green fluorescent dye. The probes need to be designed to be able to anneal to the sequence of interest and then when that sequence is amplified by PCR, the probe falls off and emits light. The more strands the probe is able to anneal to, the more probes fall off during amplification, and thus a stronger light is emitted that can be quantified like the SYBRgreen dye. Unlike SYBRgreen dye, many TaqMan probes can be used simutaneously in a sample, as each probe is specifically designed to anneal to a specific sequence and emit a different light signiture. That way the detector can distinguish one signal from another and each light signiture in the sample can be analyzed in one go.</p>
        <p>qPCR can also be used to quantitativly measure the gene copy number of a sample. In this scenario, a DNA sample is used instead of RNA. By comparing the amplification graph to another gene with a known copy number, we can estimate how many copy numbers are present in the DNA of interest compared to the gene with known copy numbers. If they both have the same copy number, we would expect the amplification curves to be the same.</p>
    
<div class="highlight-box">
<p><b>Pros:</b> easy work flow and relatively fast results.</p>
<p><b>Cons:</b> limited to known sequences and requires high-quality RNA. Can only detect known sequences.</p>
<p><em>Examples of uses in the field:</em>
<ul><li>Measuring gene expression changes in response to a drug treatment.</li>
<li>Comparing gene expression between healthy and diseased tissues.</li>
<li>Quantifying viral RNA levels in patient samples (done for covid-19).</li>
<li>Check if a mutation in a gene affects RNA splicing.</li></ul>
</div>
`,
      image: "/images/geneticMethods/qPCR.jpg",
    },
    {
      type: "question",
      text: "What is the purpose of using reverse transcriptase in qPCR?",
      options: [
        "To amplify DNA",
        "To convert RNA into cDNA",
        "To measure fluorescence",
        "To denature proteins",
      ],
      correct: 1,
      explanation:
        "Reverse transcriptase is used to convert RNA into complementary DNA (cDNA) for further PCR amplification and quantification.",
    },
    {
      type: "question",
      text: "Which fluorescent dye is commonly used in qPCR to detect double-stranded DNA?",
      options: ["TaqMan Probe", "SYBR Green", "Ethidium Bromide", "DAPI"],
      correct: 1,
      explanation:
        "SYBR Green is a fluorescent dye that emits light when it binds to double-stranded DNA, allowing for the detection and quantification of DNA during qPCR.",
    },
    {
      type: "question",
      text: "What is the main advantage of using TaqMan probes in qPCR compared to SYBR Green?",
      options: [
        "TaqMan probes are less expensive",
        "TaqMan probes can detect multiple targets simultaneously",
        "TaqMan probes do not require reverse transcription",
        "TaqMan probes are more sensitive to RNA degradation",
      ],
      correct: 1,
      explanation:
        "TaqMan probes can be designed to anneal to specific sequences and emit different light signatures, allowing for the simultaneous detection of multiple targets in a single qPCR reaction.",
    },
    {
      type: "question",
      text: "Which RNA analysis method is most suitable for quantifying specific RNA molecules?",
      options: ["qPCR", "RNA-Seq", "Microarray", "Northern Blot"],
      correct: 0,
      explanation:
        "qPCR is highly sensitive and specific, making it ideal for quantifying specific RNA molecules.",
    },
    {
      type: "text",
      content: `<h3>RNA-Seq</h3>
      <p>RNA sequencing is a technique to identify and quantify RNA transcription. RNA is isolated from a sample and thereafter a reverse transcriptase is used to make a DNA copy of all the RNA. The DNA created is called cDNA. The cDNA can thereafter be sequenced as a whole on a NGS platform. It is also possible to isolate certain types of RNA from the sample before cDNA production by using magnetic beads that bind to the RNA of interest, and thereby only examining for example mRNA.
cDNA production is vital as DNA is more stable for sequencing compared to RNA. Before sequencing starts, the sample is fragmented most often by sonication or restrictions enzymes. After fragmentation, the sample is size selected and the fragments of the best length selected for further sequencing.
</p>    
      <div class="highlight-box">
      <p><b>Pros:</b> Provides a comprehensive view of the transcriptome, can detect novel transcripts and alternative splicing events.</p>
      <p><b>Cons:</b> More expensive and time-consuming than qPCR, requires bioinformatics expertise for data analysis.</p>
      <p><em>Examples of uses in the field:</em>
      <ul><li>Identifying differentially expressed genes in disease vs. healthy tissues.</li>
      <li>Studying alternative splicing events.</li>
      <li>Characterizing the transcriptome of a new organism.</li>
      <li>Can be used to analyse variants of unknown significance (VUS), to see if these mutation affect RNA transcription. For example to see if RNA splicing is affected.</li></ul>
      </div>
      `,
      image: "/images/geneticMethods/RNASeq.jpg",
    },
    {
      type: "question",
      text: "Which RNA analysis method provides a comprehensive view of the transcriptome?",
      options: ["qPCR", "RNA-Seq", "Microarray", "Northern Blot"],
      correct: 1,
      explanation:
        "RNA-Seq provides a comprehensive view of the transcriptome, allowing for the detection of novel transcripts and alternative splicing events.",
    },
    {
      type: "question",
      text: "Which RNA analysis method is most suitable for detecting novel transcripts?",
      options: ["qPCR", "RNA-Seq", "Microarray", "Northern Blot"],
      correct: 1,
      explanation:
        "RNA-Seq allows for the detection of novel transcripts and alternative splicing events.",
    },
    {
      type: "question",
      text: "Which RNA analysis method is most suitable for detecting RNA splicing events?",
      options: ["qPCR", "RNA-Seq", "Microarray", "Northern Blot"],
      correct: 1,
      explanation:
        "RNA-Seq allows for the detection of RNA splicing events, providing insights into alternative splicing patterns.",
    },
    {
      type: "question",
      text: "Which RNA analysis method is most suitable for detecting RNA splicing events?",
      options: ["qPCR", "RNA-Seq", "Microarray", "Northern Blot"],
      correct: 1,
      explanation:
        "RNA-Seq allows for the detection of RNA splicing events, providing insights into alternative splicing patterns.",
    },
  ],
};
